import React from "react";
import Button from "@mui/material/Button";
import * as FileSaver from "file-saver";
import * as XLSX from "sheetjs-style";
import Tooltip from "@mui/material/Tooltip";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useAlert } from "../../context/AlertContext";
import logger from "../../utils/logger";

// XLSX sayfa adı 31 karakteri aşamaz ve `[ ] * ? / \` karakterlerini içeremez —
// aşarsa XLSX.write hata fırlatır ve indirme sessizce hiç başlamaz.
const MAX_SHEET_NAME_LENGTH = 31;
const sanitizeSheetName = (name) => {
  const cleaned = String(name ?? "")
    .replace(/[[\]*?/\\]/g, " ")
    .trim();
  if (!cleaned) return "Rapor";
  return cleaned.slice(0, MAX_SHEET_NAME_LENGTH).trim() || "Rapor";
};

const ExportExcel = ({
  excelData,
  fileName,
  sheetName = "Rapor",
  columnConfig = null,
  headerStyle = null,
  dataStyle = null,
  alternateRowStyle = null,
  headers = [],
  columnWidths = [],
  alignments = [],
  renderTrigger = null,
}) => {
  const { showError, showWarning } = useAlert();
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const buildAndSaveWorkbook = () => {
    const rows = Array.isArray(excelData) ? excelData : [];
    const safeSheetName = sanitizeSheetName(sheetName);

    // Sütun başlıklarını belirle
    let headerLabels =
      (headers && headers.length ? headers : null) ||
      (rows.length > 0 ? Object.keys(rows[0]) : []);

    // Veriyi Excel formatına çevir
    let ws;
    if (headerLabels.length) {
      const aoa = [
        headerLabels,
        ...rows.map((row) =>
          headerLabels.map((label) => row[label] ?? "")
        ),
      ];
      ws = XLSX.utils.aoa_to_sheet(aoa);
    } else {
      ws = XLSX.utils.json_to_sheet(rows);
    }

    // Sütun konfigürasyonu - varsayılan veya özel
    const defaultColumnConfig = {
      widths: [15, 20, 15, 18, 12, 18], // Varsayılan genişlikler
      numericColumns: ["totalConsumption", "daysCount", "dailyAverage"],
      columnMap: {
        serialNo: 0,
        firmName: 1,
        productName: 2,
        totalConsumption: 3,
        daysCount: 4,
        dailyAverage: 5,
      },
    };

    const config = columnConfig || defaultColumnConfig;

    // Sütun genişliklerini ayarla
    const resolvedColumnWidths =
      (columnWidths && columnWidths.length ? columnWidths : config.widths) ||
      [];
    const fallbackWidths =
      resolvedColumnWidths.length || !headerLabels.length
        ? resolvedColumnWidths
        : headerLabels.map(() => 20);
    const colWidths = fallbackWidths.map((width) => ({ wch: width }));
    ws["!cols"] = colWidths;

    // Varsayılan stiller
    const defaultHeaderStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, size: 12 },
      fill: { fgColor: { rgb: "667eea" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const defaultDataStyle = {
      font: { size: 11 },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } },
      },
    };

    const defaultAlternateRowStyle = {
      ...defaultDataStyle,
      fill: { fgColor: { rgb: "F8F9FA" } },
    };

    // Kullanıcı tanımlı stiller veya varsayılan stiller
    const finalHeaderStyle = headerStyle || defaultHeaderStyle;
    const finalDataStyle = dataStyle || defaultDataStyle;
    const finalAlternateRowStyle =
      alternateRowStyle || defaultAlternateRowStyle;

    const columnAlignments =
      (alignments && alignments.length ? alignments : null) ||
      columnConfig?.alignments ||
      [];

    const getAlignmentForColumn = (colIndex, fallback) =>
      columnAlignments[colIndex] || fallback || "center";

    const applyAlignment = (style, colIndex) => {
      const baseAlignment = style?.alignment || {};
      return {
        ...style,
        alignment: {
          ...baseAlignment,
          horizontal: getAlignmentForColumn(colIndex, baseAlignment.horizontal),
          vertical: baseAlignment.vertical || "center",
        },
      };
    };

    if (!ws["!ref"]) {
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: Math.max(rows.length, 1), c: headerLabels.length || 1 },
      });
    }

    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[cellAddress]) continue;

      ws[cellAddress].s = applyAlignment(finalHeaderStyle, col);
    }

    // Veri satırlarına stil uygula
    for (let row = 1; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;

        // Alternatif satırlar için farklı stil
        const isAlternateRow = (row - 1) % 2 === 1;
        const baseStyle = isAlternateRow
          ? finalAlternateRowStyle
          : finalDataStyle;
        ws[cellAddress].s = applyAlignment(baseStyle, col);
      }
    }

    // Sayısal sütunları formatla (eğer konfigürasyon varsa)
    if (config.numericColumns && config.columnMap) {
      for (let row = 1; row <= range.e.r; row++) {
        config.numericColumns.forEach((colName) => {
          const colIndex = config.columnMap[colName];
          if (colIndex !== undefined) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIndex });
            if (ws[cellAddress]) {
              // Sayısal format uygula
              if (colName === "dailyAverage") {
                ws[cellAddress].z = "0.0"; // 1 ondalık basamak
              } else {
                ws[cellAddress].z = "0"; // Tam sayı
              }
            }
          }
        });
      }
    }

    // Çalışma kitabını oluştur
    const wb = {
      Sheets: {
        [safeSheetName]: ws,
      },
      SheetNames: [safeSheetName],
    };

    // Excel dosyasını oluştur
    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  };

  const exportToExcel = () => {
    if (!Array.isArray(excelData) || excelData.length === 0) {
      showWarning("İndirilecek veri yok. Önce raporu getirin.");
      return;
    }
    try {
      buildAndSaveWorkbook();
    } catch (err) {
      // Prod build'de logger sessiz — hata kullanıcıya yalnızca toast ile
      // görünür, aksi halde butona basmak hiçbir şey yapmıyormuş gibi görünür.
      logger.error("Excel export hatası:", err);
      showError("Excel dosyası oluşturulamadı. Lütfen tekrar deneyin.");
    }
  };

  if (renderTrigger) {
    return renderTrigger(exportToExcel);
  }

  return (
    <Tooltip title="Excel'e Aktar">
      <Button
        variant="contained"
        onClick={exportToExcel}
        sx={{
          padding: "8px 16px",
          gap: "8px",
          fontSize: "14px",
          fontWeight: 500,
          textTransform: "none",
        }}
      >
        <RiFileExcel2Fill className="w-5 h-5" />
        Excel İndir
      </Button>
    </Tooltip>
  );
};

export default ExportExcel;
